/**
 * Project events (planner, P1) — WRITE request schemas (Pattern B).
 *
 * These schemas ARE THE FIELD WHITELIST for `project_events`,
 * `project_event_places`, `project_event_items`, `project_event_blocks` and
 * `person_travel_profiles`.
 * Every object is `.strict()`: zod silently strips unknown keys otherwise, so
 * a body carrying `organization_id` would 200 with the key dropped instead of
 * 400ing.
 *
 * DELIBERATELY ABSENT everywhere:
 *   - `organization_id` — from the validated `:orgId` route param.
 *   - `code`            — `EVT-{sequence}`, generated by the DB trigger.
 *   - `created_by`      — from the verified token; the INSERT policy pins it.
 * Item `item_type`, `person_id` and `asset_id` are immutable after creation,
 * so the item UPDATE schema omits them.
 *
 * The enum tuples mirror the migrations' CHECK lists
 * (`20260924130000_project_events.sql`, `20260927184230_project_event_blocks.sql`); the `satisfies` clauses tie them to
 * the pinned read types in `projectEventsReads.ts`.
 */
import { z } from "zod";
import { calendarDateSchema, localStampSchema, localTimeSchema } from "./dateWindow.js";
import { diffDaysYmd, diffLocalStampMinutes } from "../format/date.js";
/** `project_events_status_check`, lifecycle order. */
export const PROJECT_EVENT_STATUSES = ["draft", "confirmed", "completed", "cancelled"];
/** `project_event_items` item_type CHECK. */
export const PROJECT_EVENT_ITEM_TYPES = ["performance", "shift", "equipment", "setup", "strike", "break"];
/** `project_event_blocks_format_check` — arts & culture programme formats (2026-09-28). */
export const PROJECT_EVENT_BLOCK_FORMATS = [
    "performance",
    "concert",
    "showcase",
    "screening",
    "talk",
    "workshop",
    "reading",
    "opening",
    "tour",
    "rehearsal",
    "other",
];
/** `person_travel_profiles.default_travel_mode` CHECK. */
export const TRAVEL_MODES = ["bike", "cargo_bike", "walk", "train", "transit", "car", "flight"];
/** `person_travel_profiles.vehicle_type` CHECK. */
export const VEHICLE_TYPES = ["bike", "cargo_bike", "car", "van", "truck"];
/** `project_events_slot_minutes_check`. */
export const PROJECT_EVENT_SLOT_MINUTES = [15, 30, 60];
/** `project_events_max_days`: `end_date - start_date <= 13` (≤ 14 day tabs). */
export const PROJECT_EVENT_MAX_DAYS = 14;
/** Venue rows per event (soft limit, also enforced by the API on add). */
export const PROJECT_EVENT_MAX_VENUES = 10;
/** Items per POST (create / duplicate / duplicate-to-all). */
export const PROJECT_EVENT_MAX_ITEMS_PER_REQUEST = 10;
/** `project_event_items_lane_range`: a preferred lane is `0 … MAX_LANE - 1`. */
export const PROJECT_EVENT_MAX_LANE = 50;
/**
 * An item's preferred timeline lane (a layout hint — NULL = auto). The
 * planner honours it when the lane is free at the item's start.
 */
export const projectEventItemLaneSchema = z.number().int().min(0).max(PROJECT_EVENT_MAX_LANE - 1).nullable();
export const projectEventStatusSchema = z.enum(PROJECT_EVENT_STATUSES);
export const projectEventItemTypeSchema = z.enum(PROJECT_EVENT_ITEM_TYPES);
export const projectEventBlockFormatSchema = z.enum(PROJECT_EVENT_BLOCK_FORMATS);
export const travelModeSchema = z.enum(TRAVEL_MODES);
export const vehicleTypeSchema = z.enum(VEHICLE_TYPES);
export const slotMinutesSchema = z.union([z.literal(15), z.literal(30), z.literal(60)]);
const nameSchema = z.string().trim().min(1).max(255);
const descriptionSchema = z.string().max(10000).nullable();
const localThresholdKmSchema = z.number().finite().positive().max(20000);
/** Checks the date order + the 14-day cap when both ends are present. */
function datesOk(v) {
    if (v.start_date === undefined || v.end_date === undefined)
        return true;
    const d = diffDaysYmd(v.start_date, v.end_date);
    return d !== null && d >= 0 && d <= PROJECT_EVENT_MAX_DAYS - 1;
}
const DATES_MESSAGE = {
    message: `end_date must be on or after start_date and at most ${PROJECT_EVENT_MAX_DAYS} days in total`,
    path: ["end_date"],
};
/* --------------------------------- events -------------------------------- */
/**
 * POST /orgs/:orgId/projects/:projectId/events. `place_ids` become the venue
 * rows in the given order (`sort_order` = index); 1–10, no duplicates.
 */
export const createProjectEventSchema = z
    .object({
    name: nameSchema,
    description: descriptionSchema.optional(),
    start_date: calendarDateSchema,
    end_date: calendarDateSchema,
    day_start: localTimeSchema,
    day_end: localTimeSchema,
    slot_minutes: slotMinutesSchema,
    place_ids: z
        .array(z.string().uuid())
        .min(1)
        .max(PROJECT_EVENT_MAX_VENUES)
        .refine((ids) => new Set(ids).size === ids.length, "place_ids must not repeat"),
    local_threshold_km: localThresholdKmSchema.optional(),
})
    .strict()
    .refine(datesOk, DATES_MESSAGE);
/**
 * PATCH /orgs/:orgId/events/:eventId — any subset, at least one key. No
 * `place_ids` (venues have their own routes). The API writes
 * `local_threshold_km` as `settings = { local_threshold_km }`. The date check
 * only runs when both dates are sent; the DB CHECK is the backstop for a
 * one-sided change.
 */
export const updateProjectEventSchema = z
    .object({
    name: nameSchema.optional(),
    description: descriptionSchema.optional(),
    start_date: calendarDateSchema.optional(),
    end_date: calendarDateSchema.optional(),
    day_start: localTimeSchema.optional(),
    day_end: localTimeSchema.optional(),
    slot_minutes: slotMinutesSchema.optional(),
    status: projectEventStatusSchema.optional(),
    local_threshold_km: localThresholdKmSchema.optional(),
})
    .strict()
    .refine((v) => Object.keys(v).length > 0, { message: "At least one field must be provided" })
    .refine(datesOk, DATES_MESSAGE);
/* --------------------------------- venues -------------------------------- */
/** POST /orgs/:orgId/events/:eventId/venues. */
export const createProjectEventVenueSchema = z.object({ place_id: z.string().uuid() }).strict();
/** PATCH …/venues/:venueId. `green_override` null = inherit from the place. */
export const updateProjectEventVenueSchema = z
    .object({
    short_name: z.string().trim().max(60).nullable().optional(),
    green_override: z.boolean().nullable().optional(),
})
    .strict()
    .refine((v) => Object.keys(v).length > 0, { message: "At least one field must be provided" });
/** PUT …/events/:eventId/venues/order — must be exactly the event's venue ids. */
export const reorderProjectEventVenuesSchema = z
    .object({
    ids: z
        .array(z.string().uuid())
        .min(1)
        .max(PROJECT_EVENT_MAX_VENUES)
        .refine((ids) => new Set(ids).size === ids.length, "ids must not repeat"),
})
    .strict();
/* ---------------------------------- items -------------------------------- */
/** Mirrors `project_event_items_shape`. */
function itemShapeOk(v) {
    const person = v.person_id != null;
    const asset = v.asset_id != null;
    const parent = v.parent_item_id != null;
    switch (v.item_type) {
        case "performance":
        case "shift":
            return person && !asset && !parent;
        case "equipment":
            return asset && !person && !parent;
        case "setup":
        case "strike":
            return !person && !asset;
        case "break":
            return !person && !asset && !parent;
    }
}
function timesOk(v) {
    if (v.starts_at === undefined || v.ends_at === undefined)
        return true;
    const d = diffLocalStampMinutes(v.starts_at, v.ends_at);
    return d !== null && d > 0;
}
const TIMES_MESSAGE = { message: "ends_at must be after starts_at", path: ["ends_at"] };
/**
 * The block an item sits in (`project_event_items.block_id`); null = loose.
 * The API checks the block is in the event and on the item's venue; that the
 * item lies inside the block's span is a browser rule (like the window fit).
 */
const itemBlockIdSchema = z.string().uuid().nullable();
/** One item of a create request. */
export const createProjectEventItemSchema = z
    .object({
    project_event_place_id: z.string().uuid(),
    item_type: projectEventItemTypeSchema,
    person_id: z.string().uuid().nullable().optional(),
    asset_id: z.string().uuid().nullable().optional(),
    parent_item_id: z.string().uuid().nullable().optional(),
    starts_at: localStampSchema,
    ends_at: localStampSchema,
    notes: z.string().max(5000).nullable().optional(),
    lane: projectEventItemLaneSchema.optional(),
    block_id: itemBlockIdSchema.optional(),
})
    .strict()
    .refine(timesOk, TIMES_MESSAGE)
    .refine(itemShapeOk, {
    message: "performance/shift need person_id; equipment needs asset_id; setup/strike/break take neither; only setup/strike may have parent_item_id",
    path: ["item_type"],
});
/**
 * POST …/events/:eventId/items — `{ items }`, 1–10 items inserted as one
 * statement (create, duplicate, duplicate to all venues).
 */
export const createProjectEventItemsSchema = z
    .object({
    items: z.array(createProjectEventItemSchema).min(1).max(PROJECT_EVENT_MAX_ITEMS_PER_REQUEST),
})
    .strict();
/**
 * PATCH …/items/:itemId — any subset, at least one key. `item_type`,
 * `person_id` and `asset_id` are immutable. The time order is checked when
 * both ends are sent; the DB CHECK is the backstop otherwise.
 */
export const updateProjectEventItemSchema = z
    .object({
    project_event_place_id: z.string().uuid().optional(),
    starts_at: localStampSchema.optional(),
    ends_at: localStampSchema.optional(),
    notes: z.string().max(5000).nullable().optional(),
    parent_item_id: z.string().uuid().nullable().optional(),
    lane: projectEventItemLaneSchema.optional(),
    block_id: itemBlockIdSchema.optional(),
})
    .strict()
    .refine((v) => Object.keys(v).length > 0, { message: "At least one field must be provided" })
    .refine(timesOk, TIMES_MESSAGE);
/** One entry of a batch move: the item and the placement fields it changes (at least one). */
export const moveProjectEventItemSchema = z
    .object({
    item_id: z.string().uuid(),
    project_event_place_id: z.string().uuid().optional(),
    starts_at: localStampSchema.optional(),
    ends_at: localStampSchema.optional(),
    lane: projectEventItemLaneSchema.optional(),
    block_id: itemBlockIdSchema.optional(),
})
    .strict()
    .refine((v) => Object.keys(v).some((k) => k !== "item_id"), { message: "At least one field must be provided" })
    .refine(timesOk, TIMES_MESSAGE);
/**
 * POST …/events/:eventId/items/move — `{ moves }`, 1–10 items repositioned
 * in ONE statement (all or nothing): a linked group's move, or a
 * performance's resize that carries its setup / strike. Placement fields
 * only; notes / links go through the single-item PATCH.
 */
export const moveProjectEventItemsSchema = z
    .object({
    moves: z
        .array(moveProjectEventItemSchema)
        .min(1)
        .max(PROJECT_EVENT_MAX_ITEMS_PER_REQUEST)
        .refine((moves) => new Set(moves.map((m) => m.item_id)).size === moves.length, "item_id must not repeat"),
})
    .strict();
/* --------------------------------- blocks -------------------------------- */
/**
 * A new block's default length, rounded UP to the event's slot at create
 * (one default for every format).
 */
export const NEW_BLOCK_MINUTES = 60;
const blockTitleSchema = z.string().trim().min(1).max(255);
const blockNotesSchema = z.string().max(5000).nullable();
/**
 * POST …/events/:eventId/blocks. Blocks on one row never overlapping is a
 * browser rule; the API checks the venue is in the event.
 */
export const createProjectEventBlockSchema = z
    .object({
    project_event_place_id: z.string().uuid(),
    title: blockTitleSchema,
    format: projectEventBlockFormatSchema,
    starts_at: localStampSchema,
    ends_at: localStampSchema,
    notes: blockNotesSchema.optional(),
})
    .strict()
    .refine(timesOk, TIMES_MESSAGE);
/**
 * PATCH …/blocks/:blockId — any subset, at least one key. `starts_at` /
 * `ends_at` resize the block in place (its items do not move — the band's
 * edge handles); never the venue, which moves the items too (the move route,
 * one RPC). Both given: `ends_at` after `starts_at`; the API checks the
 * effective pair against the stored row. Whether the block still holds its
 * items is a browser rule.
 */
export const updateProjectEventBlockSchema = z
    .object({
    title: blockTitleSchema.optional(),
    format: projectEventBlockFormatSchema.optional(),
    notes: blockNotesSchema.optional(),
    starts_at: localStampSchema.optional(),
    ends_at: localStampSchema.optional(),
})
    .strict()
    .refine((v) => Object.keys(v).length > 0, { message: "At least one field must be provided" })
    .refine(timesOk, TIMES_MESSAGE);
/**
 * POST …/blocks/:blockId/move — the block's new venue row and start; its end
 * and every item in it shift by the same delta (`move_project_event_block`,
 * all or nothing).
 */
export const moveProjectEventBlockSchema = z
    .object({
    project_event_place_id: z.string().uuid(),
    starts_at: localStampSchema,
})
    .strict();
/** DELETE …/blocks/:blockId?items=keep|delete — keep (default) unwraps the items. */
export const deleteProjectEventBlockQuerySchema = z
    .object({ items: z.enum(["keep", "delete"]).default("keep") })
    .strict();
/* ----------------------------- travel profile ---------------------------- */
/**
 * PUT /orgs/:orgId/people/:personId/travel-profile — a full upsert.
 * `vehicle_type` only with `has_transport` (the DB CHECK's rule).
 */
export const personTravelProfileUpsertSchema = z
    .object({
    default_travel_mode: travelModeSchema.nullable(),
    has_transport: z.boolean(),
    vehicle_type: vehicleTypeSchema.nullable(),
})
    .strict()
    .refine((v) => v.has_transport || v.vehicle_type === null, {
    message: "vehicle_type requires has_transport",
    path: ["vehicle_type"],
});
//# sourceMappingURL=projectEvents.js.map